import React, { useState, useEffect } from 'react';
import { Database, Users, TrendingUp, Filter, Download, RefreshCw, Search, X, Plus } from 'lucide-react';
import BiharDataTable from './BiharDataTable';
import BiharAthleteDetailModal from './BiharAthleteDetailModal';
import { getCachedAthleteData, cacheAthleteData } from '../../utils/bihar-cache';

export default function BiharManualExplorer() {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [filteredAthletes, setFilteredAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [genderFilter, setGenderFilter] = useState('all');

  const filterTypes = [
    { id: 'height', label: 'Height (cm)', type: 'range' },
    { id: 'weight', label: 'Weight (kg)', type: 'range' },
    { id: 'vertical_jump', label: 'Vertical Jump (cm)', type: 'range' },
    { id: 'broad_jump', label: 'Broad Jump (cm)', type: 'range' },
    { id: 'sprint_30m', label: '30m Sprint (sec)', type: 'range' },
    { id: 'shuttle_run', label: 'Shuttle Run (sec)', type: 'range' },
    { id: 'football_throw', label: 'Football Throw (m)', type: 'range' },
    { id: 'run_800m', label: '800m Run (sec)', type: 'range' },
  ];

  useEffect(() => {
    loadDatasets();
  }, []);

  useEffect(() => {
    if (selectedDataset) {
      loadAthletes(selectedDataset);
    }
  }, [selectedDataset]);

  useEffect(() => {
    applyFilters();
  }, [athletes, filters, searchQuery, searchType, genderFilter]);

  const loadDatasets = async (silent = false) => {
    if (!silent) setLoadingDatasets(true);
    
    try {
      const response = await fetch('/api/bihar-datasets');
      const data = await response.json();
      const newDatasets = data.datasets || [];
      
      setDatasets(newDatasets);
      
      if (newDatasets.length > 0 && !selectedDataset) {
        setSelectedDataset(newDatasets[0].id);
      }
      
      if (selectedDataset && !newDatasets.find(d => d.id === selectedDataset)) {
        setSelectedDataset(newDatasets[0]?.id || null);
      }
    } catch (error) {
      console.error('Error loading datasets:', error);
    } finally {
      if (!silent) setLoadingDatasets(false);
    }
  };

  const loadAthletes = async (datasetId) => {
    setLoading(true);
    try {
      // Check cache first
      const cached = getCachedAthleteData(datasetId);
      if (cached) {
        setAthletes(cached.athletes || []);
        setStats(cached.stats || null);
        setLoading(false);
        return;
      }

      // Cache miss - fetch from API
      const response = await fetch(`/api/bihar-data?dataset_id=${datasetId}`);
      const data = await response.json();
      setAthletes(data.athletes || []);
      setStats(data.stats || null);
      
      // Cache the result
      cacheAthleteData(datasetId, data.athletes, data.stats);
    } catch (error) {
      console.error('Error loading athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...athletes];

    // Gender filter
    if (genderFilter !== 'all') {
      filtered = filtered.filter(athlete => {
        const g = (athlete.gender_normalized || athlete.gender || '').toString().trim().toLowerCase();
        if (genderFilter === 'male') return g === 'male' || g === 'm';
        if (genderFilter === 'female') return g === 'female' || g === 'f';
        return true;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(athlete => {
        if (searchType === 'name') {
          const fullName = (athlete.full_name || '').toLowerCase();
          const firstName = (athlete.first_name || '').toLowerCase();
          const lastName = (athlete.last_name || '').toLowerCase();
          return fullName.includes(query) || firstName.includes(query) || lastName.includes(query);
        } else if (searchType === 'id') {
          return (athlete.student_id || '').toLowerCase().includes(query);
        }
        return true;
      });
    }

    filters.forEach(filter => {
      if (filter.value && filter.value.min !== undefined && filter.value.max !== undefined) {
        filtered = filtered.filter(athlete => {
          const fieldMap = {
            height: 'height_cm',
            weight: 'weight_kg',
            vertical_jump: 'vertical_jump_cm',
            broad_jump: 'broad_jump_cm',
            sprint_30m: 'sprint_30m_sec',
            shuttle_run: 'shuttle_run_sec',
            football_throw: 'football_throw_m',
            run_800m: 'run_800m_sec'
          };
          const fieldName = fieldMap[filter.type];
          const value = athlete[fieldName];
          if (value === null || value === undefined) return false;
          return value >= filter.value.min && value <= filter.value.max;
        });
      }
    });

    setFilteredAthletes(filtered);
    calculateFilteredStats(filtered);
  };

  const calculateFilteredStats = (data) => {
    if (data.length === 0) {
      setStats(null);
      return;
    }

    const validHeights = data.filter(a => a.height_cm).map(a => a.height_cm);
    const validWeights = data.filter(a => a.weight_kg).map(a => a.weight_kg);
    const validVJumps = data.filter(a => a.vertical_jump_cm).map(a => a.vertical_jump_cm);

    const maleCount = data.filter(a => (a.gender_normalized || '').toLowerCase() === 'male').length;
    const femaleCount = data.filter(a => (a.gender_normalized || '').toLowerCase() === 'female').length;

    setStats({
      total: data.length,
      maleCount,
      femaleCount,
      avgHeight: validHeights.length > 0 ? (validHeights.reduce((a, b) => a + b, 0) / validHeights.length).toFixed(1) : 'N/A',
      avgWeight: validWeights.length > 0 ? (validWeights.reduce((a, b) => a + b, 0) / validWeights.length).toFixed(1) : 'N/A',
      avgVerticalJump: validVJumps.length > 0 ? (validVJumps.reduce((a, b) => a + b, 0) / validVJumps.length).toFixed(1) : 'N/A'
    });
  };

  const addFilter = (filterType) => {
    const newFilter = {
      id: Date.now(),
      type: filterType.id,
      label: filterType.label,
      value: { min: 0, max: 1000 }
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, value) => {
    setFilters(filters.map(f => f.id === filterId ? { ...f, value } : f));
  };

  const clearAllFilters = () => {
    setFilters([]);
    setSearchQuery('');
    setGenderFilter('all');
  };

  const displayAthletes = filteredAthletes.length > 0 || filters.length > 0 || searchQuery || genderFilter !== 'all' ? filteredAthletes : athletes;

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* LEFT PANEL - Filters (30%) */}
      <div className="col-span-4 space-y-6">
        {/* Dataset Selector */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-300">Dataset</label>
            <button
              onClick={() => loadDatasets()}
              disabled={loadingDatasets}
              className="p-1 hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 text-slate-400 ${loadingDatasets ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <select
            value={selectedDataset || ''}
            onChange={(e) => setSelectedDataset(e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {datasets.length === 0 ? (
              <option value="">No datasets available</option>
            ) : (
              datasets.map(dataset => (
                <option key={dataset.id} value={dataset.id}>
                  {dataset.name} ({dataset.record_count} records)
                </option>
              ))
            )}
          </select>
        </div>

        {/* Search Bar */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Quick Search</label>
          <div className="flex items-center space-x-2">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="name">Name</option>
              <option value="id">Student ID</option>
            </select>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search by ${searchType}...`}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Gender Filter */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Gender</label>
          <div className="flex space-x-2">
            {[{ value: 'all', label: 'All' }, { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setGenderFilter(opt.value)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  genderFilter === opt.value
                    ? opt.value === 'male' ? 'bg-blue-600 text-white' : opt.value === 'female' ? 'bg-pink-600 text-white' : 'bg-slate-600 text-white'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <label className="text-sm font-medium text-slate-300 flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {filters.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                  {filters.length}
                </span>
              )}
            </label>
            {filters.length > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Active Filters */}
          {filters.map(filter => (
            <div key={filter.id} className="mb-4 p-3 bg-slate-700/50 rounded-lg border border-slate-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">{filter.label}</span>
                <button
                  onClick={() => removeFilter(filter.id)}
                  className="p-1 hover:bg-slate-600 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Min</label>
                  <input
                    type="number"
                    value={filter.value.min}
                    onChange={(e) => updateFilter(filter.id, { ...filter.value, min: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Max</label>
                  <input
                    type="number"
                    value={filter.value.max}
                    onChange={(e) => updateFilter(filter.id, { ...filter.value, max: parseFloat(e.target.value) || 1000 })}
                    className="w-full px-3 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Add Filter Dropdown */}
          <select
            onChange={(e) => {
              if (e.target.value) {
                const filterType = filterTypes.find(ft => ft.id === e.target.value);
                addFilter(filterType);
                e.target.value = '';
              }
            }}
            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">+ Add Filter</option>
            {filterTypes.map(ft => (
              <option key={ft.id} value={ft.id}>{ft.label}</option>
            ))}
          </select>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-4">
            <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center space-x-2">
              <TrendingUp className="w-4 h-4" />
              <span>Result Summary</span>
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Showing:</span>
                <span className="text-white font-semibold">{stats.total} of {athletes.length}</span>
              </div>
              {(stats.maleCount > 0 || stats.femaleCount > 0) && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Gender:</span>
                  <span className="text-white">
                    <span className="text-blue-400">{stats.maleCount} M</span>
                    {' / '}
                    <span className="text-pink-400">{stats.femaleCount} F</span>
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Height:</span>
                <span className="text-white">{stats.avgHeight} cm</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg Weight:</span>
                <span className="text-white">{stats.avgWeight} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Avg V. Jump:</span>
                <span className="text-white">{stats.avgVerticalJump} cm</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT PANEL - Data Table (70%) */}
      <div className="col-span-8">
        {loading ? (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 flex items-center justify-center">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-400">Loading athletes...</p>
            </div>
          </div>
        ) : displayAthletes.length > 0 ? (
          <BiharDataTable 
            athletes={displayAthletes} 
            onAthleteClick={(athlete) => setSelectedAthlete(athlete)}
          />
        ) : (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">
              {filters.length > 0 || searchQuery ? 'No athletes match your filters' : 'No data available'}
            </p>
          </div>
        )}
      </div>

      {/* Athlete Detail Modal */}
      {selectedAthlete && (
        <BiharAthleteDetailModal
          athlete={selectedAthlete}
          onClose={() => setSelectedAthlete(null)}
        />
      )}
    </div>
  );
}
